// Copyright ©️ 2026 eVolve MEP, LLC

import type { HexTriplet } from '@evolvemep/api-ts';
import { Accordion, Box, Divider, Flex, LoadingOverlay, Stack, Text } from '@mantine/core';

import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { ColorPickerBar } from 'components/Mantine/ColorPickerBar';
import type { GridMenuProps } from 'components/Mantine/GridMenu';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { SelectableSpool } from 'modules/Shop/Spooling/Assigning/SelectableSpool';
import { SpoolAccordionControls } from 'modules/Shop/Spooling/Assigning/SpoolAccordionControls';
import { SpoolCard } from 'modules/Shop/Spooling/Assigning/SpoolCard';

export type SpoolAccordionProps<SpoolType, GridOptions extends string = ''> = {
  /** A label for the Accordion.Item, */
  label: string;
  /** This will be passed as the `value` prop for the underlying Accordion.Item component */
  itemValue: string;
  loading?: boolean;
  spools: Readonly<SpoolType[]> | null;
  /** A method called on the items within spools to convert them to RenderableSpool */
  getRenderableSpool: (spool: SpoolType) => RenderableSpool;
  /** Required, controls spool visibility */
  visibilityProps: {
    disabled?: boolean;
    getVisibility: (spool: SpoolType) => boolean;
    toggle: (spool: SpoolType) => void;
    toggleAll: (visible: boolean) => void;
  };
  /** If provided, a checkbox will be rendered to the left of the SpoolCard */
  selectionProps?: {
    disabled?: boolean;
    selectedSpools: RenderableSpool['name'][];
    onSpoolsSelected: (checked: boolean, ...spoolNames: RenderableSpool['name'][]) => void;
  };
  /** If provided and not disabled, a color picker will be rendered to the left of the SpoolCard */
  colorProps?: {
    disabled?: boolean;
    getColor: (spool: SpoolType) => HexTriplet;
    updateColor: (spool: SpoolType, Color: HexTriplet) => void;
  };
  getMenuProps?: (spool: SpoolType) => GridMenuProps<GridOptions>;
  /** When provided, used to determine if a spool has fabrication elements for the badge. */
  getHasFabricationElements?: (spool: SpoolType) => boolean;
};

/**
 * List of Spools for ForgeViewerFromModel, contained within an Accordion.Item
 */
export const SpoolAccordion = <SpoolType = RenderableSpool, GridOptions extends string = ''>({
  label,
  itemValue,
  loading = false,
  spools,
  getRenderableSpool,
  visibilityProps,
  selectionProps,
  colorProps,
  getMenuProps,
  getHasFabricationElements,
}: SpoolAccordionProps<SpoolType, GridOptions>) => (
  <Accordion.Item value={itemValue}>
    <SpoolAccordionControls
      label={label}
      loading={loading}
      itemCount={spools?.length ?? null}
      visibilityProps={{
        disabled: visibilityProps.disabled || (selectionProps?.selectedSpools.length ?? 0) > 0,
        visible: spools?.some((spool) => visibilityProps.getVisibility(spool)) ?? true,
        toggleAll: visibilityProps.toggleAll,
      }}
      selectionProps={
        isNotNil(selectionProps)
          ? {
              disabled: selectionProps.disabled || spools?.length === 0,
              toggleAll: () =>
                selectionProps.onSpoolsSelected(
                  selectionProps.selectedSpools.length === 0,
                  ...(spools?.map((s) => getRenderableSpool(s).name) ?? []),
                ),
              type:
                selectionProps.selectedSpools.length > 0
                  ? selectionProps.selectedSpools.length === spools?.length
                    ? 'checked'
                    : 'indeterminate'
                  : 'unchecked',
            }
          : undefined
      }
    />
    <Accordion.Panel>
      <LoadingOverlay visible={isNil(spools) || loading} />
      {isNotNil(spools) && hasLengthExactly(spools, 0) && (
        <Text c="dimmed" p="md" fz="sm">
          No results found.
        </Text>
      )}
      <Stack gap={0}>
        {spools?.map((spool) => {
          const renderableSpool = getRenderableSpool(spool);
          const commonProps = {
            spool: renderableSpool,
            isVisible: visibilityProps.getVisibility(spool),
            toggleVisibility: () => visibilityProps.toggle(spool),
          };
          return (
            <Flex key={renderableSpool.name}>
              {isNotNil(colorProps) && !colorProps.disabled && (
                <ColorPickerBar
                  label={`Spool ${renderableSpool.name} Color`}
                  value={colorProps.getColor(spool)}
                  onChange={(value) => colorProps.updateColor(spool, value)}
                  transparent={!commonProps.isVisible}
                />
              )}
              <Box style={{ flexGrow: 1 }} key={renderableSpool.name}>
                <Divider />

                {isNotNil(selectionProps) ? (
                  <SelectableSpool
                    {...commonProps}
                    disabled={selectionProps.disabled}
                    selectedSpools={selectionProps.selectedSpools}
                    onSpoolSelected={(selected) => selectionProps.onSpoolsSelected(selected, renderableSpool.name)}
                    hasFabricationElements={getHasFabricationElements?.(spool)}
                    menuProps={getMenuProps?.(spool)}
                  />
                ) : (
                  <SpoolCard
                    {...commonProps}
                    disableVisibility={visibilityProps.disabled}
                    hasFabricationElements={getHasFabricationElements?.(spool)}
                    menuProps={getMenuProps?.(spool)}
                  />
                )}
              </Box>
            </Flex>
          );
        })}
      </Stack>
    </Accordion.Panel>
  </Accordion.Item>
);
